import { ArrowLeft, ArrowRight, Building2, Check, FileCheck2, MapPin, Plus, Search, ShieldCheck, Upload } from 'lucide-react';
import { FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SelectMenu } from '../components/ui/SelectMenu';
import { LocationPickerMap } from '../components/LocationPickerMap';
import { StatusNotice } from '../components/StatusNotice';
import { api } from '../lib/api';

type HostelResult = { id: string; name: string; address?: string; landmark?: string; campus_name: string; university_name: string; match_type?: 'exact' | 'close' | 'location'; match_score?: number };
type Campus = { id: string; campus_name: string; university_name: string; city?: string; latitude?: number | null; longitude?: number | null };
type NewHostel = { name: string; campusId: string; address: string; landmark: string; latitude: number | null; longitude: number | null };
type UploadedDocument = { objectKey: string; originalFilename: string };

export function RepresentativeApplyPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preselectedId = params.get('hostelId');
  const preselectedName = params.get('hostelName') || '';
  const hostelFileRef = useRef<HTMLInputElement>(null);
  const searchRequest = useRef(0);
  const [step, setStep] = useState(1);
  const [query, setQuery] = useState(preselectedName);
  const [results, setResults] = useState<HostelResult[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [hostel, setHostel] = useState<HostelResult | null>(preselectedId ? { id: preselectedId, name: preselectedName || 'Selected hostel', campus_name: 'Selected from hostel page', university_name: '' } : null);
  const [creatingHostel, setCreatingHostel] = useState(false);
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [newHostel, setNewHostel] = useState<NewHostel>({ name: '', campusId: '', address: '', landmark: '', latitude: null, longitude: null });
  const [role, setRole] = useState('manager');
  const [name, setName] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [hostelDocumentType, setHostelDocumentType] = useState('hostel_license');
  const [hostelDocument, setHostelDocument] = useState<UploadedDocument | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    void api.get<{ campuses: Campus[] }>('/api/public/bootstrap').then(result => {
      setCampuses(result.campuses);
      setNewHostel(current => ({ ...current, campusId: current.campusId || result.campuses[0]?.id || '' }));
    }).catch(() => setError('Campus options could not be loaded. Refresh and try again.'));
  }, []);

  async function search(searchValue = query.trim()) {
    const value = searchValue.trim();
    if (value.length < 2) {
      setError('Enter at least two letters from the hostel name, area or landmark.');
      return;
    }
    const requestId = ++searchRequest.current;
    setBusy(true);
    setError('');
    setHostel(null);
    try {
      const matches = await api.get<HostelResult[]>(`/api/representative/hostels?q=${encodeURIComponent(value)}`);
      if (requestId === searchRequest.current) { setResults(matches); setHasSearched(true); }
    } catch (cause) {
      if (requestId === searchRequest.current) setError(cause instanceof Error ? cause.message : 'Search failed.');
    } finally {
      if (requestId === searchRequest.current) setBusy(false);
    }
  }

  useEffect(() => {
    const value = query.trim();
    if (creatingHostel || value.length < 2) {
      searchRequest.current += 1;
      setBusy(false);
      setResults([]);
      setHasSearched(false);
      return;
    }
    const timeout = window.setTimeout(() => void search(value), 280);
    return () => window.clearTimeout(timeout);
  }, [query, creatingHostel]);

  function searchOnEnter(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault();
      void search();
    }
  }

  function startNewHostel() {
    setCreatingHostel(true);
    setHostel(null);
    setError('');
    setNewHostel(current => ({ ...current, name: current.name || query.trim() }));
  }

  async function upload(file: File | undefined, setDocument: (document: UploadedDocument) => void) {
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const uploaded = await api.upload(file, 'hostel_document');
      setDocument({ objectKey: uploaded.objectKey, originalFilename: file.name });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Upload failed.');
    } finally {
      setBusy(false);
    }
  }

  const newHostelReady = creatingHostel
    && newHostel.name.trim().length > 1
    && Boolean(newHostel.campusId)
    && newHostel.latitude !== null
    && newHostel.longitude !== null
    && (newHostel.address.trim().length > 2 || newHostel.landmark.trim().length > 2);
  const canNext = step === 1
    ? Boolean(hostel) || newHostelReady
    : step === 2
      ? name.trim().length > 1 && phone.trim().length > 6 && note.trim().length >= 10
      : step === 3
      ? Boolean(hostelDocument)
        : accepted;

  function continueToNextStep() {
    if (!canNext) {
      setError(step === 1
        ? 'Choose a hostel, or add its name, address and exact map pin.'
        : step === 2
          ? 'Add your name, phone number and a short explanation of your relationship to the hostel.'
          : 'Upload one document that clearly shows the official hostel name.');
      return;
    }
    setError('');
    setStep(current => current + 1);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if ((!hostel && !newHostelReady) || !hostelDocument || !accepted) {
      setError('Review the required information before submitting.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await api.post('/api/representative/claims', {
        hostelId: hostel?.id,
        newHostel: hostel ? undefined : {
          campusId: newHostel.campusId,
          name: newHostel.name.trim(),
          address: newHostel.address.trim() || undefined,
          landmark: newHostel.landmark.trim() || undefined,
          latitude: newHostel.latitude,
          longitude: newHostel.longitude,
        },
        ownerName: name,
        contactPhone: phone,
        relationshipRole: role,
        organisationName: organisation || undefined,
        evidenceNote: note,
        declarationAccepted: accepted,
        documents: [{ ...hostelDocument, documentType: hostelDocumentType }],
      });
      navigate('/representative?submitted=1');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not submit your request.');
    } finally {
      setBusy(false);
    }
  }

  const selectedHostelName = hostel?.name || newHostel.name;
  const selectedCampus = campuses.find(campus => campus.id === newHostel.campusId);
  const mapCenter = { latitude: Number(selectedCampus?.latitude ?? 9.4034), longitude: Number(selectedCampus?.longitude ?? -0.8424) };

  return <div className="representative-flow market-page">
    <div className="rep-flow-top">
      <button className="text-button" onClick={() => step > 1 ? setStep(step - 1) : navigate('/representative/access')}><ArrowLeft size={17} /> Back</button>
      <span>Hostel representative verification</span><b>Step {step} of 4</b>
    </div>
    <div className="rep-progress">{[1, 2, 3, 4].map(number => <span key={number} className={number <= step ? 'active' : ''} />)}</div>
    {error && <StatusNotice type="error">{error}</StatusNotice>}
    <form className="rep-step-card" onSubmit={submit}>
      {step === 1 && <>
        <div className="representative-card-icon"><Building2 /></div>
        <h1>{creatingHostel ? 'Add your hostel for review' : 'Which hostel do you represent?'}</h1>
        <p>{creatingHostel ? 'Share the basic identity of the hostel. It will stay private and pending until HostelGrid reviews your claim.' : 'Search first so we connect your access to the correct public hostel page and avoid duplicates.'}</p>

        {!creatingHostel ? <>
          <div className="rep-search">
            <div className="rep-search-field"><Search size={19} /><input value={query} onChange={event => setQuery(event.target.value)} onKeyDown={searchOnEnter} aria-label="Search hostel catalogue" placeholder="Hostel name, area or landmark" /></div>
            <button className="secondary-button" type="button" disabled={busy} onClick={() => void search()}>{busy ? 'Searching…' : 'Search hostels'}</button>
          </div>
          <p className="rep-search-help">Try the official name, a nearby landmark, or the neighbourhood.</p>
          <div className="rep-hostel-results">
            {results.map(item => <button type="button" className={hostel?.id === item.id ? 'selected' : ''} onClick={() => { setHostel(item); setError(''); }} key={item.id}>
              <span className="rep-result-icon"><Building2 /></span>
              <span><strong>{item.name}{item.match_type === 'close' && <em>Close match</em>}{item.match_type === 'location' && <em>Area match</em>}</strong><small>{item.landmark || item.address || item.campus_name} · {item.university_name}</small></span>
              {hostel?.id === item.id ? <span className="rep-result-check"><Check /></span> : <span className="rep-result-action">Select</span>}
            </button>)}
          </div>
          {hasSearched && results.length === 0 && <div className="rep-no-results"><Search /><strong>No matching hostel found</strong><span>Check the spelling or add it for HostelGrid review.</span></div>}
          <button className="rep-not-listed" type="button" onClick={startNewHostel}><span><Plus /></span><div><strong>My hostel is not listed</strong><small>Add its name and location, then verify your relationship.</small></div><ArrowRight /></button>
        </> : <>
          <div className="rep-new-hostel-grid">
            <label><span>Hostel name</span><input value={newHostel.name} onChange={event => setNewHostel(current => ({ ...current, name: event.target.value }))} placeholder="Official hostel name" required /></label>
            <label><span>Nearest campus</span><SelectMenu value={newHostel.campusId} onChange={campusId => setNewHostel(current => ({ ...current, campusId, latitude: null, longitude: null }))} ariaLabel="Nearest campus" options={campuses.map(campus => ({ value: campus.id, label: campus.campus_name, description: campus.university_name }))} /></label>
            <label><span>Address or area</span><div className="rep-control-with-icon"><MapPin size={18} /><input value={newHostel.address} onChange={event => setNewHostel(current => ({ ...current, address: event.target.value }))} placeholder="Street, neighbourhood or area" /></div></label>
            <label><span>Nearby landmark</span><input value={newHostel.landmark} onChange={event => setNewHostel(current => ({ ...current, landmark: event.target.value }))} placeholder="For example: behind the old station" /></label>
          </div>
          <LocationPickerMap center={mapCenter} value={newHostel.latitude !== null && newHostel.longitude !== null ? { latitude: newHostel.latitude, longitude: newHostel.longitude } : null} onChange={({ latitude, longitude }) => setNewHostel(current => ({ ...current, latitude, longitude }))}/>
          <StatusNotice type="info">Add an address or landmark, then select the exact hostel entrance on the map.</StatusNotice>
          <button className="text-button rep-return-search" type="button" onClick={() => { setCreatingHostel(false); setError(''); }}><ArrowLeft size={16} /> Return to catalogue search</button>
        </>}
      </>}

      {step === 2 && <>
        <div className="representative-card-icon"><ShieldCheck /></div><h1>Tell us about your relationship</h1><p>This information is private and is used only for verification.</p>
        <div className="form-grid"><label><span>Your full name</span><input value={name} onChange={event => setName(event.target.value)} autoComplete="name" required /></label><label><span>Phone number</span><input type="tel" value={phone} onChange={event => setPhone(event.target.value)} autoComplete="tel" placeholder="e.g. 024 000 0000" required /></label><label><span>Your role</span><SelectMenu value={role} onChange={setRole} ariaLabel="Relationship to hostel" options={[{ value: 'owner', label: 'Owner' }, { value: 'manager', label: 'Manager' }, { value: 'administrator', label: 'Administrator' }, { value: 'staff', label: 'Staff member' }, { value: 'other', label: 'Other authorised person' }]} /></label><label><span>Organisation or company <small>Optional</small></span><input value={organisation} onChange={event => setOrganisation(event.target.value)} /></label></div>
        <label><span>How can we confirm your connection?</span><textarea rows={4} value={note} onChange={event => setNote(event.target.value)} placeholder="For example: I manage daily operations and the attached appointment letter is signed by the owner." required /></label>
      </>}

      {step === 3 && <>
        <div className="representative-card-icon"><FileCheck2 /></div><h1>Upload one hostel document</h1><p>The file stays private. It only needs to show the official hostel name; account numbers or financial values may be covered.</p>
        <div className="rep-document-list">
          <section className="rep-document-card">
            <div className="rep-document-heading"><span>1</span><div><h2>Official hostel-name document</h2><p>It must clearly show the same hostel name you selected or entered. An address is helpful but not required.</p></div></div>
            <label><span>Document type</span><SelectMenu value={hostelDocumentType} onChange={setHostelDocumentType} ariaLabel="Hostel identity document type" options={[{ value: 'hostel_license', label: 'GTA accommodation licence', description: 'A licence issued for the hostel or accommodation facility' }, { value: 'business_operating_permit', label: 'Business operating permit', description: 'A valid District or Municipal Assembly permit' }, { value: 'business_registration', label: 'Business registration', description: 'Registration bearing the hostel or operating business name' }, { value: 'property_tax', label: 'Property or rate document', description: 'A current document tied to the hostel premises' }, { value: 'utility_bill', label: 'Business utility bill', description: 'A bill showing the hostel name and service address' }]} /></label>
            <input ref={hostelFileRef} hidden type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={event => void upload(event.target.files?.[0], setHostelDocument)} />
            <button type="button" className={`upload-zone ${hostelDocument ? 'complete' : ''}`} onClick={() => hostelFileRef.current?.click()}><Upload /><strong>{hostelDocument ? hostelDocument.originalFilename : 'Upload hostel-name document'}</strong><span>PDF, JPG, PNG or WebP · 10 MB maximum</span></button>
          </section>
        </div>
        <StatusNotice type="info">We compare the name on this document with the selected hostel. Your verified email, contact details, relationship explanation and declaration support the manual review.</StatusNotice>
      </>}

      {step === 4 && <>
        <div className="representative-card-icon"><Check /></div><h1>Review and submit</h1>
        <div className="rep-review"><span><small>Hostel</small><strong>{selectedHostelName}</strong>{!hostel && <em>New hostel · pending review</em>}</span><span><small>Representative</small><strong>{name} · {role}</strong></span><span><small>Hostel-name evidence</small><strong>{hostelDocument?.originalFilename}</strong></span></div>
        <label className="check-row"><input type="checkbox" checked={accepted} onChange={event => setAccepted(event.target.checked)} /><span>I confirm that I am authorised to submit information for this hostel and that the information provided is accurate.</span></label>
        <StatusNotice type="info">Approval grants access only to this hostel. Public changes still pass through moderation.</StatusNotice>
      </>}

      <div className="rep-step-actions">{step < 4
        ? <button className="primary-button" type="button" onClick={continueToNextStep}>Continue <ArrowRight size={17} /></button>
        : <button className="primary-button" disabled={!canNext || busy}>{busy ? 'Submitting…' : 'Submit for review'} <ArrowRight size={17} /></button>}
      </div>
    </form>
  </div>;
}
